import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Emergent OAuth login endpoint — same URL the LoginPage uses.
const OAUTH_LOGIN_URL = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
  `${window.location.origin}/auth/callback`,
)}`;

const SILENT_RETRY_KEY = "fs_oauth_silent_retry_at";
const SILENT_RETRY_WINDOW_MS = 30 * 1000; // don't auto-retry more than once per 30s

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // session_id arrives in the URL fragment (#session_id=…) per Emergent OAuth spec
      const params = new URLSearchParams(location.hash.substring(1));
      const sessionId = params.get("session_id");

      if (!sessionId) {
        setErrorMsg("No session id in callback URL.");
        return;
      }

      let gdprConsent = false;
      let joinCode = null;
      try {
        gdprConsent = sessionStorage.getItem("gdpr_consent") === "true";
        joinCode = sessionStorage.getItem("join_code") || null;
        sessionStorage.removeItem("gdpr_consent");
        sessionStorage.removeItem("join_code");

        const response = await axios.post(
          `${API}/auth/google-session`,
          { session_id: sessionId, gdpr_consent: gdprConsent, join_code: joinCode },
          { withCredentials: true },
        );

        if (response.data.session_token) {
          localStorage.setItem("session_token", response.data.session_token);
        }
        // Successful login — clear any prior auto-retry marker
        sessionStorage.removeItem(SILENT_RETRY_KEY);

        const user = response.data.user;
        navigate("/dashboard", { replace: true, state: { user } });
      } catch (error) {
        console.error("Auth error:", error?.response?.data || error?.message || error);
        const status = error?.response?.status;
        const detail = error?.response?.data?.detail || "Sign in failed. Please try again.";

        // Silent auto-retry: Emergent's single-use session_id sometimes fails on the
        // first try (cold-start, transient 401/5xx, or network timeout). Instead of
        // forcing the user to manually click "Try again" → Google → return, we
        // transparently kick off a fresh OAuth flow. Guarded to avoid infinite loops.
        const isRetryableStatus = !status || status === 401 || status >= 500;
        if (isRetryableStatus) {
          const lastRetry = Number(sessionStorage.getItem(SILENT_RETRY_KEY) || 0);
          if (Date.now() - lastRetry > SILENT_RETRY_WINDOW_MS) {
            sessionStorage.setItem(SILENT_RETRY_KEY, String(Date.now()));
            // Preserve the user's GDPR/join-code intent across the redirect
            if (gdprConsent) sessionStorage.setItem("gdpr_consent", "true");
            if (joinCode) sessionStorage.setItem("join_code", joinCode);
            setRetrying(true);
            // Small delay so the user isn't blinked out of the page instantly
            setTimeout(() => { window.location.replace(OAUTH_LOGIN_URL); }, 400);
            return;
          }
        }
        setErrorMsg(detail);
      }
    };

    processAuth();
    // location is intentionally omitted: re-running this effect after navigate
    // would just trigger the hasProcessed guard. Keeping the dep array stable
    // also avoids edge-case StrictMode double-fires.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  if (errorMsg) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center p-6" data-testid="auth-callback-error">
        <div className="text-center max-w-sm space-y-4">
          <AlertCircle className="w-12 h-12 text-amber-400 mx-auto" />
          <h1 className="text-xl font-semibold">Sign in didn't go through</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">{errorMsg}</p>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">Please try signing in again.</p>
          <div className="flex gap-2 justify-center pt-2">
            <Button
              onClick={() => {
                // Clear the silent-retry guard so the user gets a fresh
                // auto-retry attempt instead of being blocked by the 30s window.
                sessionStorage.removeItem(SILENT_RETRY_KEY);
                window.location.replace(OAUTH_LOGIN_URL);
              }}
              data-testid="auth-retry-btn"
            >
              Try again
            </Button>
            <Button variant="outline" onClick={() => navigate("/", { replace: true })}>
              Back to home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-12 h-12 animate-spin text-[hsl(var(--primary))] mx-auto mb-4" />
        <p className="text-[hsl(var(--muted-foreground))]" data-testid="auth-callback-status">
          {retrying ? "Almost there — securing your session..." : "Completing sign in..."}
        </p>
      </div>
    </div>
  );
}
